import { useMemo, useState } from "react";
import { Cloud, Database, Shield, ArrowRight, RefreshCw, PlugZap, Loader2, DownloadCloud } from "lucide-react";
import { toast } from "sonner";
import API from "../lib/api";
import { useAudit } from "../context/AuditContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { ConnectionsCard, ConnectionsStatCard } from "../components/connections/ConnectionsLayout";
import { PageLayout } from "../components/PageLayout";

const INTEGRATIONS = [
  {
    id: "aws-s3",
    name: "AWS S3 Cloud",
    description: "Archive vault & unstructured logs",
    status: "Connected",
    statusTone: "emerald",
    metaLabel: "Last Synced",
    metaValue: "14:02:11 GMT",
    action: "Configure",
    icon: Cloud,
    iconBg: "bg-emerald-500/15",
    iconColor: "text-emerald-300",
  },
  {
    id: "sql-mainframe",
    name: "SQL Mainframe",
    description: "Transactional core DB (read-only)",
    status: "Idle",
    statusTone: "amber",
    metaLabel: "Last Synced",
    metaValue: "09:14:00 GMT",
    action: "Manual Push",
    icon: Database,
    iconBg: "bg-indigo-400/15",
    iconColor: "text-indigo-200",
  },
  {
    id: "ledger-api",
    name: "External Ledger API",
    description: "Real-time settlement gateway",
    status: "Ready",
    statusTone: "amber",
    metaLabel: "Endpoint",
    metaValue: "get_inputs",
    action: "Sync",
    icon: Shield,
    iconBg: "bg-cyan-400/15",
    iconColor: "text-cyan-200",
    actionPrimary: true,
  },
];

const STATUS_STYLES = {
  emerald: "bg-emerald-400/10 text-emerald-200 border-emerald-300/40",
  amber: "bg-amber-300/10 text-amber-200 border-amber-200/40",
  rose: "bg-rose-300/10 text-rose-200 border-rose-200/40",
};

export default function Integrations() {
  const { auditRun } = useAudit();
  const [s3Bucket, setS3Bucket] = useState("");
  const [s3Prefix, setS3Prefix] = useState("");
  const [s3Objects, setS3Objects] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [s3Loading, setS3Loading] = useState(false);
  const [s3Importing, setS3Importing] = useState(false);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerPayload, setLedgerPayload] = useState(null);
  const selectedCount = selectedKeys.length;
  const selectedSize = useMemo(
    () => s3Objects.filter((item) => selectedKeys.includes(item.key)).reduce((sum, item) => sum + (Number(item.size) || 0), 0),
    [s3Objects, selectedKeys]
  );
  const ledgerRows = Array.isArray(ledgerPayload?.results) ? ledgerPayload.results : [];

  const formatBytes = (value) => {
    const size = Number(value) || 0;
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };

  const loadS3Objects = async () => {
    if (!auditRun?.id) {
      toast.error("Select or create an audit run first.");
      return;
    }
    setS3Loading(true);
    try {
      const params = {};
      if (s3Bucket.trim()) params.bucket = s3Bucket.trim();
      if (s3Prefix.trim()) params.prefix = s3Prefix.trim();
      const { data } = await API.get(`/audit-runs/${auditRun.id}/integrations/s3/objects`, { params });
      setS3Objects(Array.isArray(data?.objects) ? data.objects : []);
      setSelectedKeys([]);
      if (data?.bucket && !s3Bucket.trim()) setS3Bucket(data.bucket);
      toast.success(`Loaded ${(data?.objects || []).length} S3 artifact(s)`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to list S3 artifacts");
    } finally {
      setS3Loading(false);
    }
  };

  const toggleKey = (key) => {
    setSelectedKeys((current) => current.includes(key) ? current.filter((item) => item !== key) : [...current, key]);
  };

  const importSelected = async () => {
    if (!auditRun?.id) {
      toast.error("Select or create an audit run first.");
      return;
    }
    if (selectedKeys.length === 0) {
      toast.error("Select at least one S3 object.");
      return;
    }
    setS3Importing(true);
    try {
      const { data } = await API.post(`/audit-runs/${auditRun.id}/integrations/s3/import`, {
        bucket: s3Bucket.trim() || undefined,
        prefix: s3Prefix.trim() || undefined,
        keys: selectedKeys,
      });
      toast.success(`Imported ${data?.imported_count || 0} artifact(s) from S3`);
      await loadS3Objects();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to import S3 artifacts");
    } finally {
      setS3Importing(false);
    }
  };

  const loadLedgerInputs = async () => {
    if (!auditRun?.id) {
      toast.error("Select or create an audit run first.");
      return;
    }
    setLedgerLoading(true);
    try {
      const { data } = await API.get(`/audit-runs/${auditRun.id}/integrations/ledger/inputs`);
      setLedgerPayload(data);
      toast.success(`Synced ${data?.total ?? data?.results?.length ?? 0} ledger input(s)`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to sync external ledger inputs");
    } finally {
      setLedgerLoading(false);
    }
  };

  const getIntegrationState = (integration) => {
    if (integration.id === "ledger-api") {
      if (ledgerLoading) {
        return { ...integration, status: "Syncing", statusTone: "amber", metaLabel: "Status", metaValue: "Fetching inputs" };
      }
      if (ledgerRows.length > 0) {
        return { ...integration, status: "Connected", statusTone: "emerald", metaLabel: "Records", metaValue: String(ledgerRows.length) };
      }
    }
    return integration;
  };

  return (
    <PageLayout
      icon={Cloud}
      title="Integrations"
      subtitle="Connect AWS S3, SQL mainframes, and external APIs so feeds stay audit-ready."
      actions={
        <Button size="sm" className="h-9 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs px-4 shadow-md shadow-emerald-600/30 border-0 active:scale-95">
          <PlugZap className="mr-1.5 h-4 w-4" /> Connect Source
        </Button>
      }
      data-testid="integrations-page"
    >

      <div className="grid gap-3 md:grid-cols-2">
        <ConnectionsStatCard value="98.4%" label="Health Score" icon={Shield} tone="emerald" />
        <ConnectionsStatCard value="03" label="Active Sync" icon={RefreshCw} />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
          {INTEGRATIONS.map((integration) => {
            const display = getIntegrationState(integration);
            const Icon = display.icon;
            return (
              <ConnectionsCard
                key={display.id}
                className="transition-all duration-300 hover:-translate-y-0.5"
                contentClassName="p-5"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="rounded-xl bg-muted p-3 text-primary dark:text-emerald-200">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className={`flex items-center gap-2 rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] ${STATUS_STYLES[display.statusTone]}`}>
                    <span className="h-1.5 w-1.5 rounded-full bg-current" />
                    {display.status}
                  </div>
                </div>

                <div className="mt-6">
                  <h3 className="text-lg font-semibold text-foreground">{display.name}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{display.description}</p>
                </div>

                <div className="mt-5 flex items-end justify-between border-t border-border pt-4">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{display.metaLabel}</p>
                    <p className="mt-1 text-xs font-semibold text-foreground">
                      {display.metaValue}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={display.id === "aws-s3" ? loadS3Objects : display.id === "ledger-api" ? loadLedgerInputs : undefined}
                    disabled={(display.id === "aws-s3" && s3Loading) || (display.id === "ledger-api" && ledgerLoading)}
                    className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] transition-all ${
                      display.actionPrimary
                        ? "bg-gradient-to-r from-primary to-emerald-800 text-primary-foreground hover:opacity-90"
                        : "text-primary hover:text-foreground dark:text-emerald-200 dark:hover:text-white"
                    } disabled:opacity-50`}
                  >
                    {display.action}
                    {(s3Loading && display.id === "aws-s3") || (ledgerLoading && display.id === "ledger-api") ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : display.actionPrimary ? <ArrowRight className="h-3.5 w-3.5" /> : <RefreshCw className="h-3.5 w-3.5" />}
                  </button>
                </div>
              </ConnectionsCard>
            );
          })}
      </div>

      <ConnectionsCard
        title="AWS S3 Artifact Sync"
        subtitle="Import CSVs and evidence artifacts into this audit."
        actions={(
            <Badge variant="outline" className="w-fit border-emerald-300/30 bg-emerald-300/10 text-emerald-100">
              {auditRun?.name ? `Active audit: ${auditRun.name}` : "No active audit"}
            </Badge>
        )}
      >

          <div className="mt-5 grid gap-3 lg:grid-cols-[1fr_1fr_auto]">
            <Input
              value={s3Bucket}
              onChange={(event) => setS3Bucket(event.target.value)}
              placeholder="Bucket from backend env, or enter bucket name"
              className="bg-white dark:bg-[#10141d]"
            />
            <Input
              value={s3Prefix}
              onChange={(event) => setS3Prefix(event.target.value)}
              placeholder="Optional prefix, e.g. audits/2026/testthree/"
              className="bg-white dark:bg-[#10141d]"
            />
            <button
              type="button"
              onClick={loadS3Objects}
              disabled={s3Loading || !auditRun?.id}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-primary px-5 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-primary transition hover:bg-emerald-50 disabled:opacity-50 dark:text-emerald-200 dark:hover:bg-emerald-950/30"
            >
              {s3Loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Browse
            </button>
          </div>

          <div className="mt-5 rounded-2xl border border-border bg-muted/50">
            {s3Objects.length === 0 ? (
              <div className="px-5 py-8 text-center text-sm text-muted-foreground">
                Browse your bucket to list supported files: CSV, Excel, PDF, Word, text, and image artifacts.
              </div>
            ) : (
              <div className="max-h-72 overflow-auto">
                {s3Objects.map((item) => (
                  <label key={item.key} className="flex cursor-pointer items-center gap-3 border-b border-border px-4 py-3 text-sm last:border-b-0 hover:bg-card dark:hover:bg-white/5">
                    <input
                      type="checkbox"
                      checked={selectedKeys.includes(item.key)}
                      onChange={() => toggleKey(item.key)}
                      disabled={item.imported}
                      className="h-4 w-4 accent-emerald-400"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-foreground">{item.filename}</p>
                      <p className="truncate text-xs text-muted-foreground">{item.key}</p>
                    </div>
                    <div className="shrink-0 text-right text-xs text-muted-foreground">
                      <p>{formatBytes(item.size)}</p>
                      {item.imported && <p className="text-emerald-300">Imported</p>}
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-muted-foreground">
              {selectedCount ? `${selectedCount} selected (${formatBytes(selectedSize)})` : "Select S3 objects to import as artifacts."}
            </p>
            <button
              type="button"
              onClick={importSelected}
              disabled={s3Importing || selectedCount === 0 || !auditRun?.id}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-primary to-emerald-800 px-5 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-primary-foreground transition hover:opacity-90 disabled:opacity-50"
            >
              {s3Importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <DownloadCloud className="h-4 w-4" />}
              Import Selected
            </button>
          </div>
      </ConnectionsCard>

      <ConnectionsCard
        title="External Ledger API"
        subtitle="Sync insurance application inputs from the backend API proxy."
        actions={(
            <button
              type="button"
              onClick={loadLedgerInputs}
              disabled={ledgerLoading || !auditRun?.id}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-primary to-emerald-800 px-5 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-primary-foreground transition hover:opacity-90 disabled:opacity-50"
            >
              {ledgerLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
              Sync Inputs
            </button>
        )}
      >

          <div className="mt-5 overflow-hidden rounded-2xl border border-border bg-muted/50">
            {ledgerRows.length === 0 ? (
              <div className="px-5 py-8 text-center text-sm text-muted-foreground">
                Sync the ledger API to preview application inputs here.
              </div>
            ) : (
              <div className="max-h-80 overflow-auto">
                <table className="w-full min-w-[760px] text-left text-sm">
                  <thead className="sticky top-0 bg-muted text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                    <tr>
                      <th className="px-4 py-3 font-semibold">Application</th>
                      <th className="px-4 py-3 font-semibold">Applicant</th>
                      <th className="px-4 py-3 font-semibold">Plan</th>
                      <th className="px-4 py-3 font-semibold">Claims</th>
                      <th className="px-4 py-3 font-semibold">Sum Assured</th>
                      <th className="px-4 py-3 font-semibold">Documents</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledgerRows.map((row, index) => {
                      const appId = row["Application ID"] || `APP-${index + 1}`;
                      const docs = [row.income_certificate_file, row.kyc_document_file, row.lab_report_file].filter(Boolean);
                      return (
                        <tr key={`${appId}-${index}`} className="border-t border-border">
                          <td className="px-4 py-3 font-mono text-xs text-destructive">{appId}</td>
                          <td className="px-4 py-3 text-foreground">{row.name || "-"}</td>
                          <td className="px-4 py-3 text-muted-foreground">{row["Plan Type"] || "-"}</td>
                          <td className="px-4 py-3 text-muted-foreground">{row["Claims Propensity"] || "-"}</td>
                          <td className="px-4 py-3 text-muted-foreground">
                            {row.sum_assured ? Number(row.sum_assured).toLocaleString() : "-"}
                          </td>
                          <td className="px-4 py-3 text-xs text-muted-foreground">{docs.length ? docs.join(", ") : "-"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
      </ConnectionsCard>
    </PageLayout>
  );
}
